import { createSelector } from 'reselect';

import { getAuthenticationState } from './selector';

/*
 * Light/dark for the new UI. Read from the redux store; saved to the user's
 * preferences on the API by the updateUser saga.
 *
 * Stored as a string rather than a boolean so 'system' -- which is neither --
 * is just another value, with no database migration.
 *
 * 'system' resolves against `prefers-color-scheme`, which lives outside redux,
 * so the *effective* theme is not a selector. Use `useDsTheme` for that.
 */

export const THEME_PREFERENCE = 'dsTheme';

export type DsTheme = 'light' | 'dark';
export type DsThemePreference = DsTheme | 'system';

/* Follow the OS unless the user picks a side. */
export const DEFAULT_THEME: DsThemePreference = 'system';

const PREFERENCES: DsThemePreference[] = ['light', 'dark', 'system'];

const isThemePreference = (value: unknown): value is DsThemePreference =>
  PREFERENCES.includes(value as DsThemePreference);

/*
 * The stored value comes from a jsonb column, so it could be any type. Anything
 * that isn't a recognised theme name falls back to the default.
 */
export const getThemePreference = createSelector(
  [getAuthenticationState],
  (authentication): DsThemePreference => {
    const stored = authentication.getIn(['user', 'preferences', THEME_PREFERENCE]);
    const theme = typeof stored === 'string' ? stored.trim().toLowerCase() : null;

    return isThemePreference(theme) ? theme : DEFAULT_THEME;
  }
);
